import { useLinked } from "@linked/react/lib";
import { styled } from "@material-ui/core";
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import { Button, div, Flex, PopupDialog, PrimaryButton, Title } from "lqx-react/lib";
import React from "react";
import { TargetFieldCard } from "../../../api/targetFieldCard";
import { ActivationButton } from "../../../components/buttons";
import { DataSheet } from "../../../components/data-sheet";
import { RightAlignedButtons } from "../../../shared";
import { importTemplate } from "../../models/import-wizard-constants";
import { MultipleSelectionsPopup } from "./multiple-selections-popup";
import { SingleSelectionPopup } from "./single-selection-popup";

export const FileReaderView = ({
    csvData,
    onSelect,
    onSelections,
    inBlockMappingFields,
    mappingItems,
    isSelectColumn,
    setSelectedColumn,
    isViewMode = false,
    isRunImportTemplateMode = false
}
    : {
        csvData: any,
        onSelect?: any,
        onSelections?: any,
        inBlockMappingFields:
        TargetFieldCard[],
        mappingItems: any,
        isSelectColumn?: boolean,
        setSelectedColumn?: any,
        isViewMode?: boolean,
        isRunImportTemplateMode?: boolean
    }) => {
    const $isPopupOpen = useLinked(false);
    const $isFullScreen = useLinked(false);
    const $mapFrom = useLinked(null).check(x => Boolean(x));
    const $isTargetHeaderRow = useLinked(false);
    const zoomStep = 2;
    const defaultFontSize = 12;
    const $zoomFontSize = useLinked(defaultFontSize);
    const zoomIn = () => { $zoomFontSize.set($zoomFontSize.value + zoomStep); };
    const zoomOut = () => { $zoomFontSize.set($zoomFontSize.value - zoomStep > zoomStep ? $zoomFontSize.value - zoomStep : zoomStep); };

    const onMultipleSelectionsApply = () => {
        $isPopupOpen.set(false);
        onSelections($mapFrom.value);
        $mapFrom.set(null);
    };

    const onSigleSelectionApply = (mapTo: string, isGlobal: boolean, globalValuePosition: string) => {
        $isPopupOpen.set(false);
        const targetFiled = inBlockMappingFields.find((item) => item.id === mapTo);
        onSelect(
            $mapFrom.value.name,
            $mapFrom.value.x,
            $mapFrom.value.y,
            mapTo,
            targetFiled.name,
            false,
            isGlobal,
            globalValuePosition,
            targetFiled.targetFieldTypeId);
        $mapFrom.set(null);
    };

    const onClose = () => {
        $mapFrom.set(null);
        $isPopupOpen.set(false);
    };

    const showPopup = (payload) => {
        $isPopupOpen.set(true);
        $mapFrom.set(payload);
    };

    const onComputeClasses = (xIndex, yIndex, classes) => {
        if (mappingItems.some(item => item.from.x === xIndex && item.from.y === yIndex)) {
            return classes + ' mapped';
        }
        return classes;
    }

    const onCellSelect = ({ data, start, end }) => {
        const cell = data[start.i][start.j];
        if (cell.value !== '' && ((start.i !== 0 && $isTargetHeaderRow.value) || (start.i !== 0 && start.j !== 0))) {
            if ($isTargetHeaderRow.value) {
                const xIndex = start.j !== 0 ? start.j : start.j + 1;
                const items = data[start.i].slice(xIndex)
                    .map((item, index) => ({
                        fromName: item.value,
                        fromX: (xIndex) + index,
                        fromY: start.i,
                        toId: importTemplate.selectOption.id,
                        toName: importTemplate.selectOption.name,
                        isGlobal: false
                    }))
                    .filter((item) => item.fromName !== '');

                if (items.length !== 0) {
                    showPopup(items);
                }
            } else {
                showPopup({ name: cell.value, x: start.j, y: start.i });
            }
        }
    };

    const onColumnSelect = ({ data, start, _ }) => {
        const cell = data[start.i][start.j];
        if (cell.value !== '') {
            setSelectedColumn({ name: cell.value, x: start.i, y: start.j });
        }
    }

    return (
        <>
            <Toolbar>
                <Flex row alignCenter stretch>
                    <ToolbarButton qa-id="zoom-in" onClick={zoomIn}><ZoomInIcon /> </ToolbarButton>
                    <ToolbarButton qa-id="zoom-out" onClick={zoomOut}><ZoomOutIcon /></ToolbarButton>
                    <ToolbarButton qa-id="full-screen" onClick={$isFullScreen.true}><ZoomOutMapIcon /></ToolbarButton>
                    {!isViewMode && <RightAlignedButtons>
                        <ActivationButton
                            {...{ active: $isTargetHeaderRow.value }}
                            onClick={() => $isTargetHeaderRow.set(!$isTargetHeaderRow.value)}>
                            Target Header Row
                        </ActivationButton>
                    </RightAlignedButtons>}
                </Flex>
            </Toolbar>
            <DataSheet
                data={csvData}
                fontSize={$zoomFontSize.value}
                onCellSelect={isSelectColumn ? onColumnSelect : onCellSelect}
                onComputeClasses={onComputeClasses}
                isViewMode={isViewMode}
                isRunImportTemplateMode={isRunImportTemplateMode}
                isFullScreen={!isRunImportTemplateMode} />

            {$isPopupOpen.value && (
                $isTargetHeaderRow.value
                    ? <MultipleSelectionsPopup
                        onMultipleSelectionsApply={onMultipleSelectionsApply}
                        onClose={onClose}
                        mappings={$mapFrom.value.map((item) => item.fromName)}
                    />
                    : <SingleSelectionPopup
                        onSigleSelectionApply={onSigleSelectionApply}
                        onClose={onClose}
                        mapping={{ mapFrom: $mapFrom.value.name }}
                        inBlockMappingFields={inBlockMappingFields}
                    />
            )}
            {$isFullScreen.value ?
                <PopupDialog
                    title={<Title> FULLSCREEN VIEW </Title>}
                    fullScreen
                    actions={
                        <PrimaryButton onClick={$isFullScreen.false}>Close</PrimaryButton>
                    }>
                    <FullscreenToolbar>
                        <Toolbar>
                            <ToolbarButton qa-id="zoom-in" onClick={zoomIn}><ZoomInIcon /> </ToolbarButton>
                            <ToolbarButton qa-id="zoom-out" onClick={zoomOut}><ZoomOutIcon /></ToolbarButton>
                        </Toolbar>
                    </FullscreenToolbar>
                    <FullscreenDocumentContainer>
                        <DataSheet
                            data={csvData}
                            fontSize={$zoomFontSize.value}
                            onCellSelect={onCellSelect}
                            onComputeClasses={onComputeClasses}
                            isViewMode={isViewMode}
                            isFullScreen={true} />
                    </FullscreenDocumentContainer>
                </PopupDialog>
                : null}
        </>
    );
};

const Toolbar = div({
    border: '1px solid #E2F0F7',
    backgroundColor: 'white'
});

const ToolbarButton = styled(Button)({
    width: '32px',
    height: '32px',
    border: 'none',
    background: 'white',
    padding: 0,
    margin: 0,
    '& .MuiSvgIcon-root': {
        fontSize: '24px',
    },
    '&:hover': {
        boxShadow: '0 0 0 0',
        border: 'none',
    },
    '&:active': {
        boxShadow: '0 0 0 0',
        border: 'none',
    },
    '&:focus': {
        boxShadow: '0 0 0 0',
        border: 'none',
    },
});

const FullscreenDocumentContainer = div({
    marginLeft: '-23px',
    height: 'calc(100% - 40px)',
    width: 'calc(100% + 23px)',
    //overflow: 'auto',
    padding: 0,
    margin: 0
});

const FullscreenToolbar = div({
    border: '1px solid #E2F0F7',
    marginLeft: '-23px',
    marginTop: '-7px',
    width: 'calc(100% + 23px)',
});
